import React, { useCallback, useMemo } from "react";
import { Dropdown } from "@packages/ui/composites/Dropdown";
import { DataTable } from "@packages/ui/patterns/DataTable";
import type { InternalActionDef } from "../schema/types";
import { evaluateIsActionable } from "../../utils/evaluateIsActionable";
import { TABLE_IDS } from "../semantic-contract";

interface ActionRendererProps {
  actions: ReadonlyArray<InternalActionDef>;
  row: Record<string, unknown>;
  rowIndex: number;
  namespace: string;
  onExecuteAction: (action: InternalActionDef, row: Record<string, unknown>, context?: SubRowContext) => void;
  checkPermission?: (permission: { action: string; resource: string }) => boolean;
  subRowContext?: SubRowContext;
}

export interface SubRowContext {
  parentRow: Record<string, unknown>;
  parentRowKey: string;
  subIndex: number;
  isSubRow: true;
}

export const ActionRenderer: React.FC<ActionRendererProps> = ({
  actions,
  row,
  rowIndex,
  namespace,
  onExecuteAction,
  checkPermission,
  subRowContext,
}) => {
  const actionStates = useMemo(
    () =>
      actions.map((action) => ({
        action,
        state: evaluateIsActionable(namespace, action, row, checkPermission),
      })),
    [actions, row, namespace, checkPermission]
  );

  const visibleEntries = actionStates.filter((a) => a.state.visible);

  const handleActionClick = useCallback(
    (action: InternalActionDef) => {
      onExecuteAction(action, row, subRowContext);
    },
    [onExecuteAction, row, subRowContext],
  );

  if (visibleEntries.length === 0) return null;

  const testId = subRowContext
    ? `dt-v2-subrow-action-menu-${subRowContext.parentRowKey}-${subRowContext.subIndex}`
    : `dt-v2-action-menu-${rowIndex}`;

  return (
    <div data-testid={testId}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <DataTable.ActionTrigger
            data-testid={`${testId}-trigger`}
            id={
              subRowContext
                ? TABLE_IDS.subRowActions(subRowContext.parentRowKey, subRowContext.subIndex)
                : TABLE_IDS.rowActions(rowIndex)
            }
          />
        </Dropdown.Trigger>
        <Dropdown.Content>
          <Dropdown.CloseButton />
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {visibleEntries.map(({ action, state }) => (
              <li key={action.id} id={action.semanticId ?? undefined}>
                <Dropdown.Item
                  onSelect={() => state.enabled && handleActionClick(action)}
                  disabled={!state.enabled}
                  semanticId={TABLE_IDS.actionItem(action.id, rowIndex)}
                  color={action.variant === 'danger' ? 'danger' : undefined}
                  icon={
                    action.icon ? (
                      <span className='material-symbols-outlined'>{action.icon}</span>
                    ) : undefined
                  }
                  title={!state.enabled ? state.reason : undefined}
                >
                  {action.label}
                </Dropdown.Item>
              </li>
            ))}
          </ul>
        </Dropdown.Content>
      </Dropdown.Root>
    </div>
  );
};

export { evaluateCondition } from '../../utils/evaluateCondition';
